import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { NovaEmpresaDialog } from "@/components/NovaEmpresaDialog";
import { PasswordConfirm } from "@/components/PasswordConfirm";
import { PrepareLaunchDialog, ModoApresentacaoDialog, PurgeEmpresaDialog } from "./DashboardDialogs";

// Ações SA do consolidado: nova empresa, excluir/purgar, preparar lançamento e Modo Apresentação (reset-all).
export function useSuperAdminActions({ refreshEmpresas, reloadCons }) {
  const [novaEmpresaOpen, setNovaEmpresaOpen] = useState(false);
  const [purgeEmpresa, setPurgeEmpresa] = useState(null); // {id, nome, count}
  const [launchEmpresa, setLaunchEmpresa] = useState(null); // {id, nome, count}
  const [demoMode, setDemoMode] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [pwdConfirm, setPwdConfirm] = useState(null);

  const excluirEmpresa = async (purge) => {
    if (!purgeEmpresa) return;
    try {
      const r = await api.delete(`/empresas/${purgeEmpresa.id}?purge=${purge ? "true" : "false"}`);
      toast.success(purge
        ? `Empresa "${purgeEmpresa.nome}" removida · ${r.data.entregas_removidas} entrega(s) apagadas`
        : `Empresa "${purgeEmpresa.nome}" removida · ${r.data.entregas_reatribuidas} entrega(s) reatribuídas para ${r.data.destino}`);
      setPurgeEmpresa(null);
      refreshEmpresas?.();
      reloadCons();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir empresa");
    }
  };

  const prepararLancamento = async () => {
    if (!launchEmpresa) return;
    try {
      const r = await api.post(`/empresas/${launchEmpresa.id}/prepare-launch`);
      toast.success(`"${launchEmpresa.nome}" pronta para apresentação · ${r.data.entregas_removidas} entrega(s), ${r.data.entregadores_removidos} entregador(es), ${r.data.usuarios_removidos} usuário(s) e ${r.data.audit_logs_removidos} log(s) removidos · cadastros e Super Admin preservados`, { duration: 8000 });
      setLaunchEmpresa(null);
      reloadCons();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao preparar lançamento");
    }
  };

  const executarModoApresentacao = async (senha) => {
    const frase = window.prompt('Esta ação apaga entregas, entregadores e usuários de TODAS as empresas (um backup completo é gerado antes). Digite RESETAR TUDO para confirmar:');
    if (!frase) return;
    setDemoLoading(true);
    try {
      const r = await api.post(`/apresentacao/reset-all`, { senha, confirmacao: frase.trim() });
      setDemoResult(r.data);
      toast.success(`Modo Apresentação: ${r.data.resumo.empresas_processadas} empresa(s) zeradas · ${r.data.resumo.entregas_removidas} entrega(s), ${r.data.resumo.usuarios_removidos} usuário(s) e ${r.data.resumo.audit_logs_removidos} log(s) removidos · backup prévio de ${r.data.resumo.backup_previo?.empresas ?? 0} empresa(s) no Cofre`, { duration: 9000 });
      reloadCons();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao ativar Modo Apresentação");
    } finally { setDemoLoading(false); }
  };

  return {
    novaEmpresaOpen, setNovaEmpresaOpen, purgeEmpresa, setPurgeEmpresa, launchEmpresa, setLaunchEmpresa,
    demoMode, demoResult, demoLoading, pwdConfirm, setPwdConfirm,
    openModoApresentacao: () => { setDemoResult(null); setDemoMode(true); },
    closeModoApresentacao: () => { setDemoMode(false); setDemoResult(null); },
    excluirEmpresa, prepararLancamento, executarModoApresentacao,
  };
}

export function SuperAdminDialogs({ sa, empresas, refreshEmpresas, reloadCons, onBaixarPdfApresentacao }) {
  return (
    <>
      <NovaEmpresaDialog open={sa.novaEmpresaOpen} onOpenChange={sa.setNovaEmpresaOpen} onCreated={() => { refreshEmpresas?.(); reloadCons(); }} />

      <PrepareLaunchDialog
        launchEmpresa={sa.launchEmpresa}
        setLaunchEmpresa={sa.setLaunchEmpresa}
        onConfirm={() => sa.setPwdConfirm({ action: sa.prepararLancamento, message: `Digite sua senha para apagar entregas, motoristas, usuários (exceto Super Admin) e auditoria de "${sa.launchEmpresa?.nome}":` })}
      />

      <ModoApresentacaoDialog
        open={sa.demoMode}
        onClose={sa.closeModoApresentacao}
        empresas={empresas}
        demoResult={sa.demoResult}
        demoLoading={sa.demoLoading}
        onExecutar={() => sa.setPwdConfirm({ action: sa.executarModoApresentacao, message: `Digite sua senha para zerar TODAS as ${empresas.length} empresas e ativar o Modo Apresentação:` })}
        onBaixarPdf={onBaixarPdfApresentacao}
      />

      <PurgeEmpresaDialog
        purgeEmpresa={sa.purgeEmpresa}
        setPurgeEmpresa={sa.setPurgeEmpresa}
        onReatribuir={() => sa.setPwdConfirm({ action: () => sa.excluirEmpresa(false), message: `Digite sua senha para reatribuir as entregas de "${sa.purgeEmpresa?.nome}":` })}
        onPurge={() => sa.setPwdConfirm({ action: () => sa.excluirEmpresa(true), message: `Digite sua senha para APAGAR ${sa.purgeEmpresa?.count} entrega(s) da empresa "${sa.purgeEmpresa?.nome}". Esta ação é IRREVERSÍVEL:` })}
      />

      <PasswordConfirm open={!!sa.pwdConfirm} onClose={() => sa.setPwdConfirm(null)}
        onConfirmed={(senha) => { sa.pwdConfirm?.action?.(senha); }} message={sa.pwdConfirm?.message} />
    </>
  );
}
